// src/components/Ticket.tsx

import React, {useState, useEffect} from 'react';
import styled, {keyframes} from 'styled-components';
import {useGame} from '../contexts/GameContext';
import {useMute} from "../contexts/MuteContext";
import ActionButton from "./buttons/actionButtion";
import {prizeStyles} from "./prizeWinnerModal";
import {audioEngine} from "./audioMap";
import {TimepassSDK} from "../assets/images/TimepassAPI/TimepassSDK";
import WinnerBanner from "./banner";
import PrizeWinner from "./prizeWinner";

/** Styled Components **/
const Container = styled.div`
    position: relative;
    background: rgba(255, 255, 255, 0.1);
    padding: 10px 10px;
    border-radius: 10px;
    margin: 5px auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (min-width: 480px) {
        width: 400px;
    }
`;


const Grid = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    padding: 14px;
    border-radius: 1rem;
    background-color: #bd1b42;
`;


const Row = styled.div`
    display: flex;
    gap: 2px;
    margin-bottom: 2px;
`;

const Cell = styled.div<{
    $isBlue: boolean;
    $isMarked: boolean;
    $hasNum: boolean;
}>`
    flex: 1;
    aspect-ratio: 1;
    position: relative;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: center;
        // background: ${({$isBlue}) => ($isBlue ? '#FFE640' : '#fff7c6')};
    backdrop-filter: blur(16.100000381469727px);
    background-color: ${props => props.$hasNum ? "white" : "#62bce6"};
    font-weight: 600;
    color: #0375aa;
    cursor: pointer;
    border-radius: 8px;
    transition: transform 0.2s ease;

    /* flower overlay when marked */
    ${({$isMarked}) =>
            $isMarked &&
            `
    &::before {
      content: "";
      position: absolute;
      width: 90%;
      height: 90%;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-image: url("./cross.png") ;
      background: rgba(0, 0, 0, 0.5)
      background-repeat: no-repeat;
      background-size: contain;
      z-index: 1;
    }
  `}
`;

const Number = styled.span<{ $isMarked: boolean }>`
    @media (min-width: 500px) {
        font-size: 1.2rem;
        font-weight: bold;
    }
    position: relative;
    font-size: 1rem;

    z-index: 2;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    ${({$isMarked}) => $isMarked && 'padding: 2px;'}

`;

const Alert = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    background: #ff4757;
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.9rem;
    z-index: 10;
`;

const ClaimButton = styled.button`
    background-color: #4CAF50;
    color: white;
    padding: 5px 15px; /* More balanced padding */
    border-radius: 5px;
    border: none;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    margin-top: 20px;
    align-self: center;

    display: flex; /* Make children flex */
    align-items: center; /* Vertically center */
    justify-content: center; /* Horizontally center */
    gap: 8px; /* Add nice space between text and icons if needed */

    &:hover {
        background-color: #45a049;
    }
`;


const FlexContainer = styled.div`
    display: flex;
    position: relative;
    gap: 10px;
    padding: 10px;
    flex-wrap: wrap; /* allows wrapping on smaller screens */
    justify-content: center; /* center-aligns the buttons */
`;

const bounce = keyframes`
    0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
    }
    40% {
        transform: translateY(-10px);
    }
    60% {
        transform: translateY(-5px);
    }
`;

const Pointer = styled.div`
    position: absolute;
    left: 25px;
    top: 25px;
    background-size: contain;
    background-repeat: no-repeat;
    animation: ${bounce} 2s infinite;
    z-index: 100000050000000000000;
`;

const Pointer2 = styled.div`
    position: absolute;
    left: 20%;
    top: 40%;
    background-size: contain;
    background-repeat: no-repeat;
    animation: ${bounce} 2s infinite;
    z-index: 10000005;
    font-family: monospace;
`;


const PointerOverlay = styled.div`
    position: absolute;
    width: 33.33%; /* cell width assuming 3 rows */
    height: 33.33%;
    pointer-events: none;
    z-index: 9999;
    transform: translate(25%, 25%);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${bounce} 2s infinite;

`;


const StyledRow = styled.div`
    display: flex;
    align-items: stretch; /* ← Ensures uniform height */
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    row-gap: 15px;
    margin-top: 10px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;

`;


const Ticket: React.FC = () => {
    const [canShowPointer, setCanShowPointer] = useState(false);
    const urlParams = new URLSearchParams(window.location.search);
    const playerId = urlParams.get('playerId');
    useEffect(() => {
        const storedIdsStr = localStorage.getItem("playerIds");
        const storedIds = storedIdsStr ? JSON.parse(storedIdsStr) : [];

        if (!storedIds.includes(playerId)) {
            localStorage.clear();

            const updatedIds = [...storedIds, playerId];
            localStorage.setItem("playerIds", JSON.stringify(updatedIds));
            localStorage.setItem("playerId", playerId as string);

            setCanShowPointer(true);
        } else {
            localStorage.setItem("playerId", playerId as string);
            const stored = localStorage.getItem("canShowPointer");
            setCanShowPointer(stored === null ? true : stored === "true");
        }
    }, [playerId]);


    const {isMuted} = useMute();
    const {
        eligiblePrizes,
        prizes,
        prizeWinners,
        calledNumbers,
        markNumber,
        ticket,
        claimPrize,
        currentNumber,
        setCheckedNumbers,
        checkedNumbers
    } = useGame();

    const [ticketGrid, setTicketGrid] = useState<(number | null)[][]>([]);

    useEffect(() => {
        if (ticket && ticket.rows) {
            const newGrid = Array.from(ticket.rows).map(row =>
                Array.from(row.numbers).map(num => num === 0 ? null : num)
            );
            // console.log("Created grid from server data:", newGrid);
            setTicketGrid(newGrid);
        }
    }, [ticket]);

    const [markedNumbers, setMarkedNumbers] = useState<number[]>([]);
    const [error, setError] = useState<string | null>(null);


    const handleCellClick = (n: number | null) => {
        if (n == null) return;
        if (calledNumbers.slice(-5).includes(n)) {
            if (!markedNumbers.includes(n)) {
                setMarkedNumbers(xs => [...xs, n]);
                setCheckedNumbers([...checkedNumbers, n])
                setCanShowPointer(false);
                localStorage.setItem("canShowPointer", "false")
                markNumber(n);
                try {
                    TimepassSDK.getInstance().sendHapticFeedback()
                } catch (e) {
                    console.error(e);
                }
            }
            if (!isMuted) {
                audioEngine.playSFX("success")
            }
        } else {
            if (!isMuted) {
                audioEngine.playSFX("reject")
            }
        }
    };

    useEffect(() => {
        if (!error) return;
        const t = setTimeout(() => setError(null), 3000);
        return () => clearTimeout(t);
    }, [error]);

    return (
        <Container>
            <div className="marquee-frame">
                <div className="border-top">
                    {[...Array(14)].map((_, i) => <div key={`top-${i}`} className="dot"/>)}
                </div>
                <div className="border-right">
                    {[...Array(5)].map((_, i) => <div key={`right-${i}`} className="dot"/>)}
                </div>
                <div className="border-bottom">
                    {[...Array(14)].map((_, i) => <div key={`bottom-${i}`} className="dot"/>)}
                </div>
                <div className="border-left">
                    {[...Array(5)].map((_, i) => <div key={`left-${i}`} className="dot"/>)}
                </div>
                <Grid>

                    <PrizeWinner/>
                    {error && <Alert>{error}</Alert>}
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: 10,
                        padding: 1,
                    }}>
                        <div style={{
                            backgroundColor: "#007EB8",
                            borderRadius: 10,
                            padding: 5,
                            margin: 5,
                        }}>
                            {typeof currentNumber === 'number' && currentNumber > 0 && canShowPointer ? (() => {
                                for (let i = 0; i < ticketGrid.length; i++) {
                                    for (let j = 0; j < ticketGrid[i].length; j++) {
                                        if (ticketGrid[i][j] === currentNumber) {
                                            return (
                                                <PointerOverlay
                                                    style={{
                                                        top: `${(i + 1) * 100 / 4}%`,
                                                        left: `${j * 100 / 11}%`
                                                    }}
                                                >
                                                    <img src="./hand-black.png" style={{width: 30, height: 40}}/>
                                                </PointerOverlay>
                                            );
                                        }
                                    }
                                }
                                return null;
                            })() : null}

                            {ticketGrid.map((row, i) => (
                                <Row key={i}>
                                    {row.map((n, j) => {
                                        let borderRadiusStyle: React.CSSProperties = {};
                                        const hasNum = n != null && n !== 0;
                                        const isBlue = (i + j) % 2 === 0;
                                        const isMarked = hasNum && markedNumbers.includes(n!);
                                        return (
                                            <Cell
                                                className={"troika"}
                                                $hasNum={hasNum}
                                                key={`${i}-${j}`}
                                                $isBlue={isBlue}
                                                $isMarked={isMarked}
                                                onClick={() => handleCellClick(n)}
                                                style={borderRadiusStyle}
                                            >
                                                {hasNum && (
                                                    <>
                                                        <Number $isMarked={isMarked}>{n}</Number>
                                                    </>
                                                )}
                                            </Cell>
                                        );
                                    })}
                                </Row>
                            ))}
                        </div>
                    </div>
                </Grid>
            </div>
            <FlexContainer>
                <StyledRow>
                    {prizes.map((n, i) => {
                        const isClaimed = prizeWinners.some((entry) => entry.prize.id === n.id && entry.player !== null);

                        return (
                            <div key={n.id} style={{width: '32%', display: 'flex'}}>
                                <ActionButton
                                    key={n.id}
                                    text={n.name}
                                    color={prizeStyles[n.name]?.color}
                                    bg={prizeStyles[n.name]?.bg}
                                    onClick={() => claimPrize(n.id)}
                                    disabled={isClaimed}
                                    eligible={eligiblePrizes.some((entry) => entry.prizeId === n.id)}
                                />
                            </div>
                        );
                    })}
                </StyledRow>

            </FlexContainer>


        </Container>
    )
        ;
};

export default Ticket;
